import { useState } from "react";
import { Lock, LogOut } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api, { clearAuthToken } from "@/lib/api";

/**
 * AccessCodeGate — modal forzato per gli utenti legacy che devono inserire
 * il nuovo codice di accesso (LPAMINDWAVE). Non chiudibile finché il codice
 * non è valido. I dati dell'utente NON vengono cancellati: solo l'accesso
 * è bloccato. L'utente può sempre fare logout.
 */
const AccessCodeGate = ({ open, userName, onVerified }) => {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const trimmed = (code || "").trim().toUpperCase();
    if (!trimmed) {
      toast.error("Inserisci il codice di accesso");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/verify-access-code", { code: trimmed });
      toast.success("Codice verificato. Bentornata!");
      onVerified?.();
    } catch (err) {
      const detail = err.response?.data?.detail || "Codice non valido";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post("/auth/logout");
    } catch { /* ignore */ }
    clearAuthToken();
    window.location.href = "/login";
  };

  return (
    <Dialog open={open}>
      <DialogContent
        className="max-w-md"
        hideCloseButton
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="access-code-gate-dialog"
      >
        <div className="flex flex-col items-center text-center space-y-3">
          <div className="w-12 h-12 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center">
            <Lock className="w-6 h-6 text-amber-700" />
          </div>
          <DialogTitle
            className="text-xl sm:text-2xl font-semibold text-stone-800"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {userName ? `Ciao ${userName.split(" ")[0]},` : "Ciao,"} serve un nuovo codice
          </DialogTitle>
          <DialogDescription className="text-sm text-stone-600 leading-relaxed">
            Per continuare a usare Revela è necessario inserire il nuovo codice
            di accesso. I tuoi dati sono al sicuro e non verranno modificati.
            Se non hai il nuovo codice, scrivi a Laura per richiederlo.
          </DialogDescription>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 pt-2">
          <Input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="Codice di accesso"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            className="text-center font-mono tracking-wider"
            data-testid="access-code-input"
          />
          <Button
            type="submit"
            disabled={submitting || !code.trim()}
            className="w-full"
            data-testid="access-code-submit"
          >
            {submitting ? "Verifica in corso..." : "Continua"}
          </Button>
        </form>

        <div className="pt-2 border-t border-stone-200">
          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-1.5 text-xs text-stone-500 hover:text-stone-700 py-1.5"
            data-testid="access-code-logout"
          >
            <LogOut className="w-3.5 h-3.5" />
            Esci dal mio account
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AccessCodeGate;
